import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { retailersApi, type Retailer } from '../services/retailersApi';
import { ApiError } from '../services/api';
import { FavoriteRetailerButton } from '../components/retailers/FavoriteRetailerButton';
import { HideRetailerButton } from '../components/retailers/HideRetailerButton';
import { RetailerRatingControl } from '../components/retailers/RetailerRatingControl';
import { Seo } from '../components/seo/Seo';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { breadcrumbList, retailerOrg } from '../utils/seo/jsonLd';

const TRUST_BREAKDOWN_BADGES = [
  'Stock accuracy',
  'Shipping transparency',
  'Return policy clarity',
  'HTTPS / site security',
  'Partner verification',
];

/**
 * Render a 1-5 star summary for a 0..1 trust score. Spec §12.2 — trust
 * "must not be presented as a guarantee", so we accompany it with a label
 * and never use a single "score" number out of context.
 */
function TrustStars({ trustScore }: { trustScore: number | string }) {
  const n = typeof trustScore === 'string' ? Number(trustScore) : trustScore;
  const safe = Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0;
  const stars = safe * 5;
  const full = Math.floor(stars);
  const half = stars - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const label = `Trust score ${safe.toFixed(2)} out of 1.00 — ${(safe * 5).toFixed(1)} of 5 stars`;
  return (
    <Chip
      color={safe >= 0.7 ? 'success' : safe >= 0.4 ? 'warning' : 'default'}
      variant="outlined"
      aria-label={label}
      label={
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          aria-hidden="true"
          sx={{ display: 'inline-flex' }}
        >
          {Array.from({ length: full }).map((_, i) => (
            <StarIcon key={`f-${i}`} fontSize="small" />
          ))}
          {half && <StarHalfIcon fontSize="small" />}
          {Array.from({ length: empty }).map((_, i) => (
            <StarBorderIcon key={`e-${i}`} fontSize="small" />
          ))}
          <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
            {safe.toFixed(2)}
          </Typography>
        </Stack>
      }
    />
  );
}

function formatDate(value: string | null): string {
  if (!value) return 'never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function ProfileCard({ retailer }: { retailer: Retailer }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-labelledby="retailer-profile-heading"
    >
      <Typography id="retailer-profile-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 2 }}>
        Profile
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        {retailer.logoUrl ? (
          <Avatar
            src={retailer.logoUrl}
            alt={`${retailer.name} logo`}
            variant="rounded"
            sx={{ width: 72, height: 72 }}
          />
        ) : (
          <Avatar variant="rounded" sx={{ width: 72, height: 72 }} aria-hidden="true">
            {retailer.name.slice(0, 2).toUpperCase()}
          </Avatar>
        )}
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>Website:</strong>{' '}
            <MuiLink
              href={retailer.baseUrl}
              target="_blank"
              rel="noopener external nofollow"
              aria-label={`Visit ${retailer.name} (opens in new tab, leaving Round Recon)`}
            >
              {retailer.baseUrl}
            </MuiLink>
          </Typography>
          <Typography variant="body2">
            <strong>Slug:</strong> {retailer.slug}
          </Typography>
          <Typography variant="body2">
            <strong>Last scraped:</strong> {formatDate(retailer.lastScrapedAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Scrape interval: every {retailer.scrapeIntervalMinutes} minutes
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
          Trust factors
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {TRUST_BREAKDOWN_BADGES.map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Detailed per-factor scores arrive in a follow-up release. Trust score is not a guarantee —
          always verify on the retailer&apos;s site before purchase.
        </Typography>
      </Box>
    </Paper>
  );
}

function getErrorState(error: unknown): { kind: 'notFound' | 'apiDown'; message: string } {
  if (error instanceof ApiError) {
    if (error.httpStatus === 404)
      return { kind: 'notFound', message: 'We couldn’t find that retailer.' };
    return { kind: 'apiDown', message: error.serverMessage || error.message };
  }
  return { kind: 'apiDown', message: (error as Error)?.message ?? 'Unknown error' };
}

/**
 * Retailer detail page (spec §12.1).
 *
 * Displays the retailer profile (logo, base URL, trust factors), a trust
 * score chip (§12.2), a degraded-banner when applicable (§28.5), the
 * indexed-listing count with a "Browse {N} listings" CTA, and an
 * inactive-retailer notice.
 */
export function RetailerDetailPage() {
  const { id } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ['retailer', id],
    queryFn: () => retailersApi.getRetailer(id ?? ''),
    enabled: Boolean(id),
    retry: false,
  });

  if (!id) {
    return (
      <Alert severity="error" role="alert">
        Missing retailer id in the URL.
      </Alert>
    );
  }

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading retailer" />
      </Box>
    );
  }

  if (query.isError) {
    const { kind, message } = getErrorState(query.error);
    return (
      <Alert severity={kind === 'notFound' ? 'warning' : 'error'} role="alert">
        {kind === 'notFound'
          ? 'Retailer not found. '
          : 'We couldn’t load this retailer right now. '}
        <Typography component="span" variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Alert>
    );
  }

  if (!query.data) return null;

  const { retailer, listingCount, ratingSummary } = query.data;
  const canonical = buildCanonicalUrl(`/retailers/${retailer.slug ?? retailer.id}`);
  const seoDescription =
    `${retailer.name} ammunition inventory on Round Recon. ` +
    `${listingCount} listings indexed. Compare prices per round, freshness, and stock against other retailers.`;

  return (
    <Stack spacing={3} component="article" aria-labelledby="retailer-heading">
      <Seo
        title={`${retailer.name} Ammo Inventory`}
        description={seoDescription}
        canonical={canonical}
        ogType="website"
        jsonLd={[
          retailerOrg({
            name: retailer.name,
            slug: retailer.slug ?? retailer.id,
            url: canonical,
            description: seoDescription,
            logo: retailer.logoUrl ?? undefined,
          }),
          breadcrumbList([
            { name: 'Round Recon', url: '/' },
            { name: 'Retailers', url: '/retailers' },
            { name: retailer.name, url: canonical },
          ]),
        ]}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        flexWrap="wrap"
      >
        <Typography id="retailer-heading" variant="h1" sx={{ fontSize: '2rem' }}>
          {retailer.name}
        </Typography>
        <TrustStars trustScore={retailer.trustScore} />
        <FavoriteRetailerButton retailerId={retailer.id} retailerName={retailer.name} />
        <HideRetailerButton retailerId={retailer.id} retailerName={retailer.name} />
        {!retailer.isActive && (
          <Chip
            color="default"
            variant="outlined"
            label="No longer indexed"
            aria-label="Retailer no longer indexed"
          />
        )}
      </Stack>

      {!retailer.isActive && (
        <Alert severity="info" role="status">
          This retailer is no longer indexed by Round Recon. Listings shown elsewhere on the site
          may be out of date.
        </Alert>
      )}

      {retailer.isDegraded && (
        <Alert severity="warning" role="alert" data-testid="degraded-banner">
          We&apos;re currently having trouble scraping <strong>{retailer.name}</strong>. Stock and
          price data from this retailer may be out of date until the issue is resolved.
        </Alert>
      )}

      <ProfileCard retailer={retailer} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <RetailerRatingControl
          retailerId={retailer.id}
          retailerName={retailer.name}
          summary={ratingSummary}
        />
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: 2 }}
        component="section"
        aria-labelledby="listings-heading"
      >
        <Typography id="listings-heading" variant="h2" sx={{ fontSize: '1.25rem', mb: 1 }}>
          Listings
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          We currently track <strong>{listingCount.toLocaleString()}</strong>{' '}
          {listingCount === 1 ? 'listing' : 'listings'} from {retailer.name}.
        </Typography>
        <Button
          component={RouterLink}
          to={`/search?retailerId=${encodeURIComponent(retailer.id)}`}
          variant="contained"
          disabled={listingCount === 0 || !retailer.isActive}
        >
          Browse {listingCount.toLocaleString()} listings from {retailer.name}
        </Button>
      </Paper>
    </Stack>
  );
}

export default RetailerDetailPage;
